import { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery } from '@mui/material';
import { Lightbulb } from '@phosphor-icons/react';

import {
  HsaEligibleBoxStyled,
  PremiumDetailPillStyled,
  FallbackDetailsBoxStyled,
} from './PlanDetailsStyled';
import { PlanDetailsDrawer } from '../PlanDetailsDrawer';
import {
  BenefitPlanDto,
  PcpMembersDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { getStyledPremiumCovLevels } from 'enrollment/features/Plans/planDetailsUtils/planDetailsUtils';
import { mapDefaultFactiods } from './utils';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { FieldValueDto, FieldWithValueDto } from 'theSource/types';
import { PrimaryCarePlanDetails, CoverageLevelSelect } from './components';
import {
  PlanInfoResponseDto,
  PlanLevelCostDto,
} from 'enrollment/queries/usePlanInformationQuery/utils';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { selectPerPayPeriodText } from 'enrollment/services';
import { AdditionalPlanElectionDataDto } from './types';
import {
  getPremiumStyled,
  RETURN_DISPLAY,
  RETURN_VALUE_ONLY,
} from 'enrollment/utils';
import { TooltipWrapper } from 'enrollment/components/Shared/TooltipWrapper';
import { PsCollapse } from 'stencilSetup';

interface PlanDetailsProps {
  benefitLookup?: string;
  plan: BenefitPlanDto;
  subscriber?: FieldWithValueDto;
  dependents?: DependentsResponseDto;
  selectedDependentIds?: number[];
  pcpMembers?: PcpMembersDto;
  selected: boolean;
  electPlan: () => void;
  setPcpElectionData: (data: AdditionalPlanElectionDataDto) => void;
  togglePcpDrawer: () => void;
  openChooseProviderDrawer: boolean;
  setOpenChooseProviderDrawer: (value: boolean) => void;
  setShowPcpDrawer: (value: boolean) => void;
  setAllowPcpElection: (value: boolean) => void;
  isDepSelect?: boolean;
  planInfoData: PlanInfoResponseDto | undefined;
  isLoadingPlanInfo: boolean;
  isFetchingPlanInfo: boolean;
  showCovLevSelect: boolean;
  selectedCovLevel: string;
  setSelectedCovLevel: Dispatch<SetStateAction<string>>;
  planId?: number;
  isPcpOptional: boolean;
  isEsignPlanRequired?: boolean | undefined;
  isFindAProviderEnabled?: boolean;
  hideDependentCheckboxWhenChecked: { [key: string]: boolean };
  setHideDependentCheckboxWhenChecked: (value: {
    [key: string]: boolean;
  }) => void;
  subscribeZipCode?: FieldValueDto | undefined;
  setPcpDrawerRequired?: (value: boolean) => void;
  hideEmptyCost: boolean;
}

export const PlanCardDetails = ({
  benefitLookup,
  plan,
  subscriber,
  dependents,
  selectedDependentIds,
  pcpMembers,
  selected,
  electPlan,
  setPcpElectionData,
  openChooseProviderDrawer,
  setOpenChooseProviderDrawer,
  setShowPcpDrawer,
  setAllowPcpElection,
  togglePcpDrawer,
  isDepSelect,
  planInfoData,
  isLoadingPlanInfo,
  isFetchingPlanInfo,
  showCovLevSelect,
  selectedCovLevel,
  setSelectedCovLevel,
  planId,
  isPcpOptional = false,
  isEsignPlanRequired,
  isFindAProviderEnabled,
  hideDependentCheckboxWhenChecked,
  setHideDependentCheckboxWhenChecked,
  subscribeZipCode,
  setPcpDrawerRequired,
  hideEmptyCost,
}: PlanDetailsProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const perPayText = useSelector(selectPerPayPeriodText);
  const [isTruncated, setIsTruncated] = useState(false);
  const annualDetailsRef = useRef<HTMLDivElement>(null);

  // Utility function to generate a unique ID
  const generateUniqueId = () =>
    `id-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  // Generate a unique ID for this instance of PlanCardDetails
  const uniqueId = useRef(generateUniqueId()).current;

  const factiod = benefitLookup && mapDefaultFactiods(benefitLookup);
  let factoidItems: Array<string> = [];
  const planInfoProps = { planInfoData, isLoadingPlanInfo, isFetchingPlanInfo };

  if (factiod) {
    factoidItems = t(`benefitFactiods.${factiod}`, { returnObjects: true });
  }
  useEffect(() => {
    if (annualDetailsRef.current) {
      const textContent = annualDetailsRef.current.textContent || '';
      const truncated = isTextTruncated(textContent);
      setIsTruncated(truncated);
    }
  }, []);

  const isTextTruncated = (text: string): boolean => {
    const isDevice = window.innerWidth < 1024;
    if (!isDevice) {
      return text.trim().length > 63;
    } else {
      return false;
    }
  };

  const showPrimaryCarePlanDetails =
    plan.pcp_data && (benefitLookup === 'dental' || 'medical') ? true : false;

  const fixedVolEoi = Boolean(plan.volume) && plan.requires_eoi;
  const isDesktop = useMediaQuery('(min-width: 1199px)');

  const renderPremiumValue = () => {
    if (isDepSelect) {
      if (hideEmptyCost && Number(plan.subscriber_premium) === 0) {
        return '';
      } else {
        return getPremiumStyled({
          premium: plan.subscriber_premium,
          valueOnly: RETURN_DISPLAY,
          tagName: 'div',
          dollarClasses: 'headline-2 font-weight-normal',
        });
      }
    } else {
      return getStyledPremiumCovLevels(
        planInfoData?.data.plan_level_costs,
        selectedCovLevel,
        hideEmptyCost
      );
    }
  };

  return (
    <>
      <div className="plan-card-details d-flex flex-column flex-lg-row justify-content-between pt-4 pb-3_5 px-5">
        <div className="plan-premium-box d-flex flex-column justify-content-between justify-content-md-start text-center mb-4 mb-md-0">
          {typeof renderPremiumValue() === 'object' ? (
            <>
              {renderPremiumValue()}
              <p className="body1 mb-0">{`${perPayText}`}</p>
            </>
          ) : (
            <div style={{ minHeight: '88px' }}></div>
          )}
          {(!plan.volume || Number(plan.volume) === 0) &&
            plan.content_details.length > 0 && (
              <PlanDetailsDrawer
                plan={plan}
                {...planInfoProps}
                hideEmptyCost={hideEmptyCost}
              />
            )}
          {plan.volume && Number(plan.volume) > 0 && (
            <>
              <Typography fontSize={16} fontWeight={400} className="mt-5">
                {t('CovAmount')}
              </Typography>
              <div className="h3">
                {getPremiumStyled({
                  premium: plan.volume,
                  valueOnly: RETURN_VALUE_ONLY,
                })}
              </div>
            </>
          )}
          <div className="d-flex flex-column justify-content-between w-100">
            {showCovLevSelect && (
              <CoverageLevelSelect
                planLevelCosts={
                  planInfoData?.data.plan_level_costs as PlanLevelCostDto
                }
                selectedCovLevel={selectedCovLevel}
                setSelectedCovLevel={setSelectedCovLevel}
              />
            )}
            {plan.high_deductible && (
              <HsaEligibleBoxStyled alignItems="center">
                <Lightbulb
                  size={20}
                  color="#d16627"
                  style={{ marginRight: '16px' }}
                />
                <Typography fontSize={14}>{t('HsaEligible')}</Typography>
              </HsaEligibleBoxStyled>
            )}
          </div>
        </div>
        {/* if plan details configured and not a factoid plan show them */}
        {!factiod && plan.content_details.length > 1 && (
          <div className="plan-addn-info d-flex flex-column flex-md-row">
            {/* loop for first two configured content options */}
            {plan.content_details.slice(0, 2).map((detail, index) => (
              <div
                key={index}
                className="card rounded-lg shadow-none bg-orbit-grey border-orbit-grey mt-0 mb-2 mb-lg-0 px-3 pt-3 pb-2 pt-lg-2 pb-lg-4"
              >
                <PsCollapse
                  collapseId={`${uniqueId}-collapse-${index}`}
                  caret={true}
                  hideOnViewport="tablet"
                  className="plan-card-flex"
                >
                  <div slot="heading" className="flex-fill">
                    <p className="body2 text-left" ref={annualDetailsRef}>
                      {isTruncated ? (
                        <TooltipWrapper
                          position="top"
                          tooltipContent={detail.label}
                          tooltipContentId={`tooltipContentId-${index}`}
                          allowWrap={true}
                        >
                          <button className="truncate clamp-2">
                            {detail.label}
                          </button>
                        </TooltipWrapper>
                      ) : (
                        detail.label
                      )}
                    </p>
                  </div>
                  <div slot="content" className="pb-3 pb-lg-0">
                    {/* loop for in network and out of network fields */}
                    {[detail.in_network, detail.out_of_network].map(
                      (networkType, index) => (
                        <div
                          className="prices"
                          style={{ marginTop: '16px' }}
                          key={index}
                        >
                          <span className="body1 semiBold flex-fill">
                            {networkType ? networkType : '-'}
                          </span>
                          <PremiumDetailPillStyled>
                            {t(
                              index === 0 ? 'InNetworkCaps' : 'OutOfNetworkCaps'
                            )}
                          </PremiumDetailPillStyled>
                        </div>
                      )
                    )}
                  </div>
                </PsCollapse>
              </div>
            ))}
          </div>
        )}
        {/* if a factoid plan show them */}
        {factiod && (
          <FallbackDetailsBoxStyled>
            <p className="body2">{t('keyInfo')}</p>
            {factoidItems.map((item, index) => (
              <ul
                key={index}
                style={{ listStyleType: 'disc' }}
                className="body semiBold factoid"
              >
                <li>{item}</li>
              </ul>
            ))}
          </FallbackDetailsBoxStyled>
        )}
        {/* for agreement benefits and misconfigurations: if not factoid and no benefit content show blank */}
      </div>
      {showPrimaryCarePlanDetails && (
        <PrimaryCarePlanDetails
          subscriber={subscriber}
          dependents={dependents}
          selectedDependentIds={selectedDependentIds}
          pcpMembers={pcpMembers}
          selected={selected}
          benefitLookup={benefitLookup}
          electPlan={electPlan}
          setPcpElectionData={setPcpElectionData}
          setAllowPcpElection={setAllowPcpElection}
          togglePcpDrawer={togglePcpDrawer}
          openChooseProviderDrawer={openChooseProviderDrawer}
          setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
          setShowPcpDrawer={setShowPcpDrawer}
          planId={Number(planId)}
          isPcpOptional={isPcpOptional}
          plan={plan}
          isEsignPlanRequired={isEsignPlanRequired}
          isFindAProviderEnabled={isFindAProviderEnabled}
          hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
          setHideDependentCheckboxWhenChecked={
            setHideDependentCheckboxWhenChecked
          }
          subscribeZipCode={subscribeZipCode}
          setPcpDrawerRequired={setPcpDrawerRequired}
        />
      )}
      {fixedVolEoi && (
        <div className="d-flex w-100 justify-content-center">
          <div
            className="pt-2"
            style={{ padding: `${isDesktop ? '20px 80px' : '20px 40px'}` }}
          >
            <PSBannerWrapper
              id="eoi-notice"
              data-testid="eoi-notice"
              message={t('pages.volume:eoiNotice')}
              level="info"
              icon={true}
            />
          </div>
        </div>
      )}
    </>
  );
};
